import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { displayProduct } from "../../lib/products";

/**
 * Station-overrides audit report (added June 2026).
 *
 * Surfaces every customer × station-scope margin rule on a single screen with
 * its drift vs the same customer's default-scope margin for the same network
 * + product. The June 2026 Gill Force Carrier Inc. / Whitby incident
 * (station 32512 silently billed at 3 ¢/L instead of the group default
 * 6 ¢/L) was caused by a stale station override that never propagated. This
 * tile makes that class of drift visible at-a-glance.
 *
 * Lifecycle column:
 *   • active  — effective_date ≤ as_of AND (no end_date OR end_date ≥ as_of)
 *   • pending — effective_date >  as_of   (staged for a future date)
 *   • expired — end_date < as_of           (the new B.4 schema constraint)
 */
export default function StationOverridesReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asOf, setAsOf] = useState("");
  const [includeExpired, setIncludeExpired] = useState(false);
  const [q, setQ] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (asOf) params.set("as_of", asOf);
      if (includeExpired) params.set("include_expired", "true");
      const { data } = await api.get(
        `/margins/station-overrides-report${params.toString() ? `?${params}` : ""}`,
      );
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load station overrides report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOf, includeExpired]);

  const filtered = useMemo(() => {
    const rows = report?.rows || [];
    const ql = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (lifecycleFilter && r.lifecycle !== lifecycleFilter) return false;
      if (!ql) return true;
      const hay = [
        r.customer_name, r.pricing_group_name, r.station_id, r.station_name,
        r.city, r.province, r.network, r.product_canonical,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(ql);
    });
  }, [report, q, lifecycleFilter]);

  const driftBadge = (drift) => {
    if (drift === null || drift === undefined) return <span className="text-zinc-400">—</span>;
    const v = Number(drift);
    if (Math.abs(v) < 0.01) return <Badge tone="zinc">matches default</Badge>;
    const tone = v < 0 ? "rose" : "amber";
    const sign = v > 0 ? "+" : "";
    return <Badge tone={tone}>{`${sign}${v.toFixed(4)} ¢/L`}</Badge>;
  };

  const lifecycleBadge = (lc) => {
    if (lc === "expired") return <Badge tone="zinc">expired</Badge>;
    if (lc === "pending") return <Badge tone="blue">pending</Badge>;
    return <Badge tone="emerald">active</Badge>;
  };

  return (
    <SectionPad>
      <PageHeader
        title="Station-override audit"
        subtitle="Every customer × station-scope margin override, with drift vs the same customer's default for the same network + product. Catches the class of bug where a pricing-group uplift didn't propagate to a station that carried a stale manual override."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <input
          className="gf-input md:col-span-2"
          placeholder="Search customer, station, city, group…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="station-overrides-search"
        />
        <div className="flex items-center gap-2">
          <span className="overline">EFFECTIVE ON</span>
          <input
            type="date"
            className="gf-input"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            data-testid="station-overrides-as-of"
            title="Compute drift using rules in effect on this date (default: today)"
          />
        </div>
        <select
          className="gf-input"
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
          data-testid="station-overrides-lifecycle"
        >
          <option value="">All lifecycle states</option>
          <option value="active">Active only</option>
          <option value="pending">Pending only</option>
          <option value="expired">Expired only</option>
        </select>
      </div>
      <div className="flex justify-between items-center mb-2 gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
            data-testid="station-overrides-include-expired"
          />
          Include expired (end_date in the past)
        </label>
        <div className="text-xs font-mono text-zinc-500" data-testid="station-overrides-summary">
          {loading
            ? "LOADING…"
            : report
              ? `${filtered.length} OF ${report.count} OVERRIDES · ${report.with_drift} WITH DRIFT · AS OF ${report.as_of}`
              : ""}
        </div>
      </div>

      <div className="gf-card">
        <table className="gf-table">
          <thead>
            <tr>
              <th>CUSTOMER</th>
              <th>PRICING GROUP</th>
              <th>NET</th>
              <th>PRODUCT</th>
              <th>STATION</th>
              <th className="text-right">STATION ¢/L</th>
              <th className="text-right">DEFAULT ¢/L</th>
              <th>DRIFT</th>
              <th>EFFECTIVE</th>
              <th>END</th>
              <th>LIFECYCLE</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={11} className="text-center text-zinc-400 py-8">
                {report && report.count === 0
                  ? "No station-scope overrides exist in the system. Pricing-group defaults apply everywhere."
                  : "No overrides match the current filters."}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.margin_id} data-testid={`station-override-row-${r.margin_id}`}>
                <td>
                  <Link
                    to={`/customers/${r.customer_id}`}
                    className="text-zinc-900 hover:underline"
                    title="Open customer"
                  >
                    {r.customer_name}
                  </Link>
                </td>
                <td className="text-xs">
                  {r.pricing_group_name || <span className="text-zinc-400">—</span>}
                </td>
                <td className="font-mono text-xs uppercase">{r.network}</td>
                <td className="font-mono text-xs">{displayProduct(r.product_canonical, r.network) || "ANY"}</td>
                <td className="text-xs">
                  <span className="font-mono">{r.station_id}</span>
                  {r.station_name && <span className="text-zinc-500"> · {r.station_name}</span>}
                  {r.city && <span className="text-zinc-400"> · {r.city}{r.province ? `, ${r.province}` : ""}</span>}
                </td>
                <td className="gf-num font-mono">
                  {Number(r.station_margin_cpl ?? 0).toFixed(2)}
                  {r.pricing_mode === "replace" && (
                    <Badge tone="emerald" style={{ marginLeft: 6 }}>FIXED</Badge>
                  )}
                </td>
                <td className="gf-num font-mono">
                  {r.default_margin_cpl === null
                    ? <span className="text-zinc-400">—</span>
                    : Number(r.default_margin_cpl).toFixed(2)}
                </td>
                <td>{driftBadge(r.drift_cpl)}</td>
                <td className="font-mono text-xs">{r.effective_date}</td>
                <td className="font-mono text-xs">
                  {r.end_date || <span className="text-zinc-400">—</span>}
                </td>
                <td>{lifecycleBadge(r.lifecycle)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionPad>
  );
}
